export type RibbonRovingKey = "ArrowLeft" | "ArrowRight" | "Home" | "End";

export const RIBBON_BAND_ATTR = "data-ribbon-band";
export const RIBBON_ROVING_ATTR = "data-ribbon-roving";
export const RIBBON_ROVING_GROUP_ATTR = "data-ribbon-roving-group";
export const RIBBON_NATIVE_TAB_ATTR = "data-ribbon-native-tab";
export const RIBBON_MEASURE_ATTR = "data-ribbon-measure";
export const RIBBON_PLACEHOLDER_ATTR = "data-ribbon-placeholder";

const DOCK_RIBBON_PORTAL_ID = "dock-ribbon-portal";

const GROUP_FOCUSABLE_SELECTOR = "button, a[href], input, select, textarea";

export const isRibbonNativeTabItem = (element: HTMLElement): boolean =>
	element.closest(`[${RIBBON_NATIVE_TAB_ATTR}]`) !== null;

const isEligibleRovingItem = (element: HTMLElement): boolean =>
	!element.closest(`[${RIBBON_MEASURE_ATTR}]`) &&
	(!isRibbonNativeTabItem(element) || element.getAttribute("aria-hidden") !== "true") &&
	!(
		element instanceof HTMLInputElement &&
		isRibbonNativeTabItem(element) &&
		element.getAttribute("tabindex") === "-1"
	) &&
	!element.hasAttribute("disabled") &&
	element.getAttribute("aria-disabled") !== "true" &&
	!element.hasAttribute("hidden") &&
	!element.closest("[hidden]") &&
	!(element instanceof HTMLInputElement && element.type === "hidden");

const nextRovingIndex = (current: number, key: RibbonRovingKey, count: number): number => {
	if (count <= 0) {
		return -1;
	}
	const clamped = Math.min(Math.max(current, 0), count - 1);
	switch (key) {
		case "ArrowRight":
			return (clamped + 1) % count;
		case "ArrowLeft":
			return (clamped - 1 + count) % count;
		case "Home":
			return 0;
		case "End":
			return count - 1;
	}
};

export const collectRibbonRovingItems = (band: HTMLElement): HTMLElement[] => {
	const nodes = Array.from(
		band.querySelectorAll<HTMLElement>(`[${RIBBON_ROVING_ATTR}], [${RIBBON_ROVING_GROUP_ATTR}]`)
	);
	const items: HTMLElement[] = [];
	const seen = new Set<HTMLElement>();
	const push = (element: HTMLElement) => {
		if (!seen.has(element)) {
			seen.add(element);
			items.push(element);
		}
	};
	for (const node of nodes) {
		if (node.hasAttribute(RIBBON_ROVING_GROUP_ATTR)) {
			for (const focusable of node.querySelectorAll<HTMLElement>(GROUP_FOCUSABLE_SELECTOR)) {
				push(focusable);
			}
		} else {
			push(node);
		}
	}
	return items.filter(isEligibleRovingItem);
};

export const resolveActiveRibbonBand = (root: ParentNode = document): HTMLElement | null => {
	const dockBand = root.querySelector<HTMLElement>(`#${DOCK_RIBBON_PORTAL_ID} [${RIBBON_BAND_ATTR}]`);
	if (dockBand) {
		return dockBand;
	}
	return root.querySelector<HTMLElement>(`[${RIBBON_BAND_ATTR}]:not([${RIBBON_PLACEHOLDER_ATTR}])`);
};

export const focusActiveRibbonBand = (root: Document = document): boolean => {
	const band = resolveActiveRibbonBand(root);
	if (!band) {
		return false;
	}
	const items = collectRibbonRovingItems(band);
	if (items.length === 0) {
		return false;
	}
	const target =
		items.find((item) => item.tabIndex === 0 && !isRibbonNativeTabItem(item)) ??
		items.find((item) => item.tabIndex === 0) ??
		items[0];
	target.focus();
	return true;
};

export default nextRovingIndex;
